import React, { useState, useEffect } from 'react';
import { Select, Input, Button, message, Tooltip, DatePicker } from 'antd';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CalendarFilled } from '@ant-design/icons';


const { Option } = Select;

const fieldMapping = {
  "Advisor Fee": "advisor_fee",
  "Recordkeeper Fee": "record_keeper_fee",
  "TPA Fee": "tpa_fee",
  "Funds Fee": "funds_fee",
  "Date Confirmed": "date_confirmed"
};

const AutomaticDataEntryForFeeSchedule = ({ onAddRow, onDataSubmitted }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    advisor_fee: '',
    record_keeper_fee: '',
    tpa_fee: '',
    funds_fee: '',
    date_confirmed: dayjs().format('YYYY-MM-DD')
  });

  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyData, setCompanyData] = useState([]);

  // Fetch client names when component mounts
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');

        // Year is a string of the current year
        const year = dayjs().year();
        console.log("Year:", year);
       

        
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: {
            'user-id': userId
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setCompanyOptions(data.client_names);
      } catch (error) {
        console.error('Error fetching client names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
  }, []);

  // Fetch Fee Schedule Data
  useEffect(() => {
    const fetchFeeScheduleData = async () => {
      try {
        const userId = localStorage.getItem('userid');
        const year = dayjs().year();
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/spreadsheet/${userId}/Fee Schedule/${year}`, {
          headers: {
            'user-id': userId
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // console.log("Received fee schedule data:", data);
        setCompanyData(data.data || []);
      } catch (error) {
        console.error('Error fetching fee schedule data:', error);
        message.error('Failed to load fee schedule data');
      }
    };

    fetchFeeScheduleData();
  }, []);

  // Pre-fill formData when company_name or companyData changes
  useEffect(() => {
    if (formData.company_name && companyData.length > 0) {
      const selectedCompany = companyData.find(c => c["Company Name"] === formData.company_name);
      if (selectedCompany) {
        setFormData(prevFormData => {
          const updatedData = { ...prevFormData };

          for (const [spreadsheetKey, formKey] of Object.entries(fieldMapping)) {
            if (selectedCompany.hasOwnProperty(spreadsheetKey) && selectedCompany[spreadsheetKey] !== null) {
              let value = selectedCompany[spreadsheetKey];

              if (formKey === 'date_confirmed' && value) {
                let parsedDate = null;
                
                if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                  parsedDate = value;
                } else {
                  const dateParts = value.split('/');
                  if (dateParts.length === 3) {
                    const [mm, dd, yyyy] = dateParts;
                    if (!isNaN(mm) && !isNaN(dd) && !isNaN(yyyy)) {
                      parsedDate = `${yyyy.padStart(4,'0')}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;
                    }
                  }
                }

                updatedData[formKey] = parsedDate || value;
              } else {
                updatedData[formKey] = value;
              }
            }
          }

          return updatedData;
        });
      }
    }
  }, [formData.company_name, companyData]);

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleDateChange = (date, dateString) => {
    setFormData(prev => ({
      ...prev,
      date_confirmed: dateString
    }));
  };

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem('userid');
      const year = dayjs().year();

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/fee-schedule/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'year': year,
          'user-id': userId,
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      message.success(data.message || 'Fee schedule updated successfully');

      // Clear form after successful submission
      setFormData({
        company_name: '',
        advisor_fee: '',
        record_keeper_fee: '',
        tpa_fee: '',
        funds_fee: '',
        date_confirmed: dayjs().format('YYYY-MM-DD')
      });

      if (onAddRow) {
        onAddRow(formData);
      }

      if (onDataSubmitted) {
        onDataSubmitted();
      }
    } catch (error) {
      console.error('Error submitting fee schedule:', error);
      message.error('Failed to submit fee schedule');
    }
  };

  const isFormValid = () => {
    return formData.company_name !== '';
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-2 bg-white rounded-2xl">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Fee Schedule Data Entry</h1>
      <form className="space-y-6">
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Company Name", "Select the company for which you're setting up the fee schedule.")}
          </label>
          <Select
            value={formData.company_name}
            onChange={(value) => handleChange('company_name', value)}
            style={{ width: '100%' }}
            placeholder="Select company"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            optionFilterProp="children"
          >
            {companyOptions.map(company => (
              <Option key={company} value={company}>{company}</Option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Advisor Fee", "Enter the fee charged by the advisor (percentage or flat rate).")}
            </label>
            <Input
              value={formData.advisor_fee}
              onChange={(e) => handleChange('advisor_fee', e.target.value)}
              placeholder="Enter advisor fee"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Recordkeeper Fee", "Enter the fee charged by the Recordkeeper for their services.")}
            </label>
            <Input
              value={formData.record_keeper_fee}
              onChange={(e) => handleChange('record_keeper_fee', e.target.value)}
              placeholder="Enter Recordkeeper fee"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("TPA Fee", "Enter the fee charged by the Third Party Administrator.")}
            </label>
            <Input
              value={formData.tpa_fee}
              onChange={(e) => handleChange('tpa_fee', e.target.value)}
              placeholder="Enter TPA fee"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Funds Fee", "Enter the total fees associated with the investment funds.")}
            </label>
            <Input
              value={formData.funds_fee}
              onChange={(e) => handleChange('funds_fee', e.target.value)}
              placeholder="Enter funds fee"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Date Confirmed", "Select the date when these fees were confirmed or last updated.")}
          </label>
          <DatePicker
            value={formData.date_confirmed ? dayjs(formData.date_confirmed) : null}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            className="w-full"
            placeholder="Select date"
            suffixIcon={<CalendarFilled size={16} className="text-gray-400" />}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.375rem'
            }}
          />
        </div>

        <div className="flex justify-start">
          <Tooltip title="Save this fee schedule to the database">
            <Button 
              onClick={handleSubmit}
              disabled={!isFormValid()}
              size="large"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white ${isFormValid() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
            >
              <DocumentIcon className="h-5 w-5 mr-2" />
              Submit Fee Schedule
            </Button>
          </Tooltip>
        </div>
      </form>
    </div>
  );
};

export default AutomaticDataEntryForFeeSchedule;
